import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../services/category.service';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { uia1 } from '../assets/data/thumbnails/tb';
import { ps1, ps2, ps3, ps4, ps5, sericon } from '../assets/assets';

const ServicesWeOffer = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Fetch categories
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const categories = categoriesData?.categories || [];
  const totalPages = Math.ceil(categories.length / itemsPerPage);
  
  // Get current page items
  const currentCategories = categories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // List of top categories for filter buttons
  const topCategories = categories.slice(0, 7).map(c => c.name);

  // Function to convert title to URL-friendly format
  const titleToSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
  };

  // Function to handle service category click
  const handleCategoryClick = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName);
    if (cat) {
      window.location.href = `/category/${cat.slug}`;
    }
  };

  // Function to handle service card click
  const handleServiceClick = (cat) => {
    window.location.href = `/category/${cat.slug}`;
  };

  // Function to handle page navigation
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Generate pagination numbers
  const generatePaginationNumbers = () => {
    const numbers = [];
    
    // Show first page
    numbers.push(1);
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      numbers.push('...');
    }
    
    // Show current page and surrounding pages
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i !== 1 && i !== totalPages) {
        numbers.push(i);
      }
    }
    
    // Show ellipsis if needed
    if (currentPage < totalPages - 2) {
      numbers.push('...');
    }
    
    // Show last page
    if (totalPages > 1) {
      numbers.push(totalPages);
    }
    
    return numbers;
  };

  return (
    <div className="bg-gray-50 min-h-screen font-['Poppins']">
      <div className="container mx-auto px-4 py-6 md:py-10">
        {/* Main header */}
        <h1 className="text-2xl md:text-3xl font-bold text-[#174568] mb-4 md:mb-8">All Services</h1>
        
        {/* Filter buttons */}
        <div className="md:w-[90%] mx-auto grid grid-cols-2 md:grid-cols-4 items-center gap-2 md:gap-3 mb-6 md:mb-10">
          {topCategories.map((category, index) => (
            <button
              key={index}
              onClick={() => handleCategoryClick(category)}
              className="px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition cursor-pointer"
            >
              {category}
            </button>
          ))}
        </div>
        
        {/* Service Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10 md:mb-16">
          {isLoading ? (
             Array.from({ length: 8 }).map((_, i) => (
               <div key={i} className="h-64 bg-gray-200 animate-pulse rounded-md"></div>
             ))
          ) : currentCategories.map((category) => (
            <div 
              key={category._id} 
              className="flex flex-col cursor-pointer transform hover:scale-105 transition-transform duration-200"
              onClick={() => handleServiceClick(category)}
            >
              {/* Card with image and title */}
              <div className="relative h-32 md:h-40 lg:h-48 bg-gray-800 rounded-md overflow-hidden mb-2 hover:shadow-lg transition-shadow">
                <img 
                  src={category.thumbnail || ps1} 
                  alt={category.name}
                  className="w-full h-full object-cover opacity-75" 
                />
                <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-2 md:p-4">
                  
                    {category.icon && category.icon.includes('/') ? (
                    <img
                      src={category.icon}
                      alt={category.name}
                      className="w-[2rem] h-[2rem] object-cover opacity-60"
                    />
                  ) : (
                    <div className="text-3xl mb-2">{category.icon || "🔧"}</div>
                  )}

                  <h3 className="text-xs md:text-sm lg:text-base font-bold text-center uppercase">
                    {category.name}
                  </h3>
                </div>
              </div>
              
              {/* Specialist info replaced with gig count */}
              <div className="flex justify-between items-center px-1">
                <div>
                  <p className="text-sm font-medium text-gray-800">{category.gigCount} Services</p>
                  <p className="text-[10px] text-gray-500">Explore  {category.name} Services</p>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination */}
        <div className="flex justify-center items-center space-x-2 md:space-x-3">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full border ${
              currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-[#185BC3] border-gray-300 hover:bg-gray-100'
            }`}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          
          {generatePaginationNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
              className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full ${
                page === currentPage
                  ? 'text-[#185BC3]'
                  : page === '...'
                  ? 'text-gray-500 cursor-default'
                  : 'text-gray-700'
              }`}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full border ${
              currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-[#185BC3] border-gray-300 hover:bg-gray-100'
            }`}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServicesWeOffer;